import FormTextField from '../../../../shared/FormTextField';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Divider,
    Grid,
    InputLabel,
    Stack,
    Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import BusinessIcon from '@mui/icons-material/Business';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import PolicyIcon from '@mui/icons-material/Policy';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import type { ReactNode } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useGetIndividualContractorQuery } from '../../../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../../../shared/AuthRoles';
import ServiceAreas from './ServiceAreas';
import Services from './Services';
import AddressDetails from './AddressDetails';
import PolicyDetails from './PolicyDetails';
import Availability from './Availability';
import { Period } from '../../../../types/contractor';
import ProfileSectionCard from '../../ProfileSectionCard';
import ProfileReadOnlyField from '../../ProfileReadOnlyField';
import { useEditProfileNavigation } from '../../useEditProfileNavigation';
import { useProfileInlineUx } from '../../ProfileInlineUxContext';
import type { ProfileInlineEditSection } from '../../ProfileInlineUxContext';
import {
    contractorSaveShouldWarnServiceTypeChange,
    filterContractorServiceItemsForSelectedType,
} from '../../profileInlineSectionDirty';

interface ContractorCompanyDetailsProps {
    isEditing: boolean;
    modernProfileView?: boolean;
}

const ContractorCompanyDetails = ({
    isEditing,
    modernProfileView = false,
}: ContractorCompanyDetailsProps) => {
    const loggedInUser = UserRole();
    const { control, getValues, reset, setValue } = useFormContext();
    const [serviceTypeChangeDialogOpen, setServiceTypeChangeDialogOpen] =
        useState(false);
    const { isDirty } = useFormState({ control });

    const nameW = useWatch({ control, name: 'name' });
    const abnW = useWatch({ control, name: 'abn' });
    const primaryContactW = useWatch({ control, name: 'primaryContact' });
    const phoneW = useWatch({ control, name: 'phone' });
    const emailW = useWatch({ control, name: 'email' });

    const { data: contractor } = useGetIndividualContractorQuery(
        loggedInUser?.company,
        { skip: !loggedInUser?.company },
    );

    const { canShowEdit } = useEditProfileNavigation(true);
    const {
        suspendContractorResets,
        inlineEditSection,
        beginInlineEdit,
        cancelInlineEdit,
        saveInlineEdit,
        inlineEditSnapshotRef,
    } = useProfileInlineUx();
    const saveContractorServicesInline = useCallback(() => {
        const services = getValues('services');
        if (
            contractorSaveShouldWarnServiceTypeChange(
                inlineEditSnapshotRef.current,
                services,
            )
        ) {
            setServiceTypeChangeDialogOpen(true);
            return;
        }
        saveInlineEdit();
    }, [getValues, inlineEditSnapshotRef, saveInlineEdit]);

    const confirmSaveAfterServiceTypeChange = useCallback(() => {
        setServiceTypeChangeDialogOpen(false);
        const services = getValues('services') as
            | { type?: string; items?: string[] }
            | undefined;
        const type = services?.type;
        const nextItems = filterContractorServiceItemsForSelectedType(
            type,
            services?.items,
        );
        setValue('services.items', nextItems, {
            shouldDirty: true,
            shouldValidate: true,
        });
        saveInlineEdit();
    }, [getValues, setValue, saveInlineEdit]);

    useEffect(() => {
        if (suspendContractorResets) return;
        reset({
            ...getValues(),
            ...contractor,
            cutoff: contractor?.cutoff ?? '',
            unavailablePeriods:
                contractor?.unavailablePeriods?.map((period: Period) => ({
                    ...period,
                    demo_id: Math.random(),
                })) || [],
        });
    }, [contractor, suspendContractorResets, reset, getValues]);

    const shell = Boolean(modernProfileView);
    const readOnlyShell = shell && !isEditing;

    const orgEditing =
        !shell && isEditing ? true : inlineEditSection === 'contractor_company';
    const servicesEditing =
        !shell && isEditing
            ? true
            : inlineEditSection === 'contractor_services';
    const termsEditing =
        !shell && isEditing ? true : inlineEditSection === 'contractor_terms';
    const availabilityEditing =
        !shell && isEditing
            ? true
            : inlineEditSection === 'contractor_availability';

    const contractorReadOnlyTop = (
        <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ rowGap: 0.5, mb: 1 }}>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileReadOnlyField label='Company'>
                    {nameW}
                </ProfileReadOnlyField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileReadOnlyField label='ABN'>{abnW}</ProfileReadOnlyField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileReadOnlyField label='Primary contact'>
                    {primaryContactW}
                </ProfileReadOnlyField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileReadOnlyField label='Phone'>
                    {phoneW}
                </ProfileReadOnlyField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
                <ProfileReadOnlyField label='Email'>
                    {emailW}
                </ProfileReadOnlyField>
            </Grid>
        </Grid>
    );

    const contractorOrgEditFields = (
        <Grid container columnSpacing='50px' rowSpacing='10px'>
            <Grid item container xs={12} md={6}>
                <Grid item xs={12} lg={5}>
                    <InputLabel required>Company</InputLabel>
                </Grid>
                <Grid item xs={12} lg={7}>
                    <FormTextField
                        name='name'
                        control={control}
                        disabled={!orgEditing}
                    />
                </Grid>
            </Grid>
            <Grid item container xs={12} md={6}>
                <Grid item xs={12} lg={5}>
                    <InputLabel required>ABN</InputLabel>
                </Grid>
                <Grid item xs={12} lg={7}>
                    <FormTextField
                        name='abn'
                        control={control}
                        disabled={!orgEditing}
                    />
                </Grid>
            </Grid>
            <Grid item container xs={12} md={6}>
                <Grid item xs={12} lg={5}>
                    <InputLabel required>Primary Contact</InputLabel>
                </Grid>
                <Grid item xs={12} lg={7}>
                    <FormTextField
                        name='primaryContact'
                        control={control}
                        disabled={!orgEditing}
                    />
                </Grid>
            </Grid>
            <Grid item container xs={12} md={6}>
                <Grid item xs={12} lg={5}>
                    <InputLabel required>Phone</InputLabel>
                </Grid>
                <Grid item xs={12} lg={7}>
                    <FormTextField
                        name='phone'
                        control={control}
                        disabled={!orgEditing}
                    />
                </Grid>
            </Grid>
            <Grid item container xs={12} md={6}>
                <Grid item xs={12} lg={5}>
                    <InputLabel required>Email</InputLabel>
                </Grid>
                <Grid item xs={12} lg={7}>
                    <FormTextField
                        name='email'
                        control={control}
                        disabled={!orgEditing}
                    />
                </Grid>
            </Grid>
            <Grid item xs />
        </Grid>
    );

    const contractorShellReadOnly = (
        <>
            {contractorReadOnlyTop}
            <Services isEditing={false} />
            <ServiceAreas isEditing={false} />
            <Availability isEditing={false} />
            <PolicyDetails isEditing={false} />
        </>
    );

    const contractorBody = (
        <>
            <Grid container columnSpacing='50px' rowSpacing='10px'>
                <Grid item container xs={12} md={6}>
                    <Grid item xs={12} lg={5}>
                        <InputLabel required>Company</InputLabel>
                    </Grid>
                    <Grid item xs={12} lg={7}>
                        <FormTextField
                            name='name'
                            control={control}
                            disabled={!isEditing}
                        />
                    </Grid>
                </Grid>
                <Grid item container xs={12} md={6}>
                    <Grid item xs={12} lg={5}>
                        <InputLabel required>ABN</InputLabel>
                    </Grid>
                    <Grid item xs={12} lg={7}>
                        <FormTextField
                            name='abn'
                            control={control}
                            disabled={!isEditing}
                        />
                    </Grid>
                </Grid>
                <Grid item container xs={12} md={6}>
                    <Grid item xs={12} lg={5}>
                        <InputLabel required>Primary Contact</InputLabel>
                    </Grid>
                    <Grid item xs={12} lg={7}>
                        <FormTextField
                            name='primaryContact'
                            control={control}
                            disabled={!isEditing}
                        />
                    </Grid>
                </Grid>
                <Grid item container xs={12} md={6}>
                    <Grid item xs={12} lg={5}>
                        <InputLabel required>Phone</InputLabel>
                    </Grid>
                    <Grid item xs={12} lg={7}>
                        <FormTextField
                            name='phone'
                            control={control}
                            disabled={!isEditing}
                        />
                    </Grid>
                </Grid>
                <Grid item container xs={12} md={6}>
                    <Grid item xs={12} lg={5}>
                        <InputLabel required>Email</InputLabel>
                    </Grid>
                    <Grid item xs={12} lg={7}>
                        <FormTextField
                            name='email'
                            control={control}
                            disabled={!isEditing}
                        />
                    </Grid>
                </Grid>
                <Grid item xs />
            </Grid>

            <AddressDetails isEditing={isEditing} />
            <Services isEditing={isEditing} />
            <ServiceAreas isEditing={isEditing} />
            <Availability isEditing={isEditing} />
            <PolicyDetails isEditing={isEditing} />
        </>
    );

    const sectionCard = (
        section: Exclude<ProfileInlineEditSection, null>,
        icon: ReactNode,
        title: string,
        subtitle: string,
        editLabel: string,
        children: ReactNode,
        onSaveInline?: () => void,
        defaultCollapsed?: boolean,
    ) => {
        return (
            <ProfileSectionCard
                icon={icon}
                title={title}
                subtitle={subtitle}
                editButtonLabel={editLabel}
                onEdit={() => beginInlineEdit(section)}
                showEditButton={canShowEdit}
                inlineEditActive={inlineEditSection === section}
                onCancelInline={cancelInlineEdit}
                onSaveInline={onSaveInline ?? saveInlineEdit}
                saveDisabled={inlineEditSection === section && !isDirty}
                defaultCollapsed={defaultCollapsed}
            >
                {children}
            </ProfileSectionCard>
        );
    };

    if (shell) {
        return (
            <Stack spacing={3}>
                <Dialog
                    open={serviceTypeChangeDialogOpen}
                    onClose={() => setServiceTypeChangeDialogOpen(false)}
                    aria-labelledby='contractor-service-type-change-title'
                    maxWidth='sm'
                    fullWidth
                >
                    <DialogTitle id='contractor-service-type-change-title'>
                        Change service type?
                        <IconButton
                            onClick={() =>
                                setServiceTypeChangeDialogOpen(false)
                            }
                            sx={{ color: 'text.primary' }}
                            aria-label='close'
                        >
                            <Close />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant='body2' color='text.secondary'>
                            Saving will remove the services you had selected for
                            your previous service type. You can only offer one
                            service type at a time.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button
                            onClick={() =>
                                setServiceTypeChangeDialogOpen(false)
                            }
                        >
                            Cancel
                        </Button>
                        <Button
                            variant='contained'
                            onClick={confirmSaveAfterServiceTypeChange}
                        >
                            Save
                        </Button>
                    </DialogActions>
                </Dialog>
                {sectionCard(
                    'contractor_company',
                    <BusinessIcon sx={{ fontSize: 22 }} />,
                    'Company details',
                    'Company, ABN, primary contact, phone, email, and business address.',
                    'Edit company details',
                    readOnlyShell || !orgEditing ? (
                        <Stack spacing={2.5}>
                            {contractorReadOnlyTop}
                            <AddressDetails isEditing={false} companyShell />
                        </Stack>
                    ) : (
                        <Stack spacing={2.5}>
                            {contractorOrgEditFields}
                            <AddressDetails
                                isEditing={orgEditing}
                                companyShell
                            />
                        </Stack>
                    ),
                )}
                {sectionCard(
                    'contractor_services',
                    <DesignServicesIcon sx={{ fontSize: 22 }} />,
                    'Services',
                    'Service type, services offered, and service locations.',
                    'Edit services',
                    <Box
                        sx={{
                            mx: { xs: -2, sm: -3 },
                            mt: { xs: -2, sm: -3 },
                            mb: { xs: -2, sm: -3 },
                            px: { xs: 2, sm: 3 },
                            py: { xs: 2, sm: 3 },
                        }}
                    >
                        <Stack spacing={2.5}>
                            <Services
                                isEditing={servicesEditing}
                                compactShell
                            />
                            <Divider />
                            <ServiceAreas
                                isEditing={servicesEditing}
                                compactShell
                            />
                        </Stack>
                    </Box>,
                    saveContractorServicesInline,
                    true,
                )}
                {sectionCard(
                    'contractor_availability',
                    <CalendarMonthIcon sx={{ fontSize: 22 }} />,
                    'Availability',
                    'Available days, job request cut-offs, and periods you are unavailable.',
                    'Edit availability',
                    <Availability
                        isEditing={availabilityEditing}
                        compactShell
                    />,
                    undefined,
                    true,
                )}
                {sectionCard(
                    'contractor_terms',
                    <PolicyIcon sx={{ fontSize: 22 }} />,
                    'Terms and conditions',
                    'Cancellation policy and related terms.',
                    'Edit terms',
                    <PolicyDetails isEditing={termsEditing} compactShell />,
                    undefined,
                    true,
                )}
            </Stack>
        );
    }

    return (
        <>
            <Typography
                sx={{
                    fontWeight: 'bold',
                    color: 'text.primary',
                    py: '30px',
                }}
            >
                Company Details
            </Typography>
            {contractorBody}
        </>
    );
};

export default ContractorCompanyDetails;
